import { Injectable } from '@angular/core';
import { Account, User } from '@mitel/cloudlink-sdk';
import { AppInjector } from './../../app-injector';
import { AccountsService } from './accounts.service';
import { AppUserClaimsService } from './app-user-claims.service';
import { SpinnerService } from './spinner.service';
import { AdminService } from './admin.service';
import { MenuItem } from '../interfaces/menu-item';
import { IntegrationItemServiceInf, StatusString, UserLicense } from './../interfaces/integrations';
import { UserRoles } from '../constants/constants';
import { ConfirmationDialogComponent } from '../../shared/components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { ConfirmationDialogs } from '../../shared/components/dialogs/confirmation-dialog/confirmation-dialogs.enum';
import { MatDialog } from '@angular/material/dialog';
import { IntegCustomerAdminPortalService } from './integ-customer-admin-portal.service';
import { IntegMiteamMeetingsService } from './integ-miteam-meetings.service';
import { StatusType } from '../constants/statusType.enum';
import { OrdersService } from './orders.service';
import { mivoiceBusinessLicenses, productCodes } from '../constants/constants';
import { UsersBulkEditMiVBService } from './users-bulk-edit-mivb.service';
import { UserMiVBSharedService } from './user-mivb-shared.service';
import { MiVBAccountSettingsService } from './mivb-account-settings.service';
import { Util } from '../utils/Util';
import { FeatureToggleService } from './feature-toggle.service';
import { FeatureFlags } from '../../core/interfaces/feature-flags';
import { UIPreviewKey, UIPreviewToggleService } from './ui-preview/ui-preview-toggle.service';
import { ZappaAccountService } from './zappa-account.service';

@Injectable({
	providedIn: 'root'
})
export class IntegMiVoiceBusinessService implements IntegrationItemServiceInf {

	private accountsService: AccountsService = undefined;
	private adminService: AdminService = undefined;
	private appClaimsService: AppUserClaimsService = undefined;
	private spinnerService: SpinnerService = undefined;
	private capService: IntegCustomerAdminPortalService = undefined;
	private meetService: IntegMiteamMeetingsService = undefined;
	public dialog: MatDialog;
	private orderService: OrdersService;
	private userBulkEditService: UsersBulkEditMiVBService;
	private mivbSharedSvc: UserMiVBSharedService;
	private capIsEnabled: boolean = false; 
	private capPromise: Promise<boolean> = undefined;
	private currentAccountId: string = undefined;
	private mivbAccountSettingsService: MiVBAccountSettingsService; 
	private mivbInNetworkElements: boolean  = false;
	private mivbInNetworkElementsPromise: Promise<boolean> = undefined;
	private initPromises: Promise<any>[] = [];
	private unexpectedPbxExists: boolean | undefined = undefined;
	private featureToggleService: FeatureToggleService = undefined;
	private uiPreviewToggleService: UIPreviewToggleService = undefined;
	private zappaAccountService: ZappaAccountService;

	constructor() 
	{ }

	async initServices( account: Account ): Promise<any> {

		if (!this.accountsService)
			this.accountsService = AppInjector.injector.get(AccountsService);

		if (!this.adminService)
			this.adminService = AppInjector.injector.get(AdminService);

		if (!this.featureToggleService)
			this.featureToggleService = AppInjector.injector.get(FeatureToggleService);

		if (!this.capService)
		{
			this.capService = AppInjector.injector.get(IntegCustomerAdminPortalService);
		}

		if ( !this.mivbAccountSettingsService )
			this.mivbAccountSettingsService = AppInjector.injector.get(MiVBAccountSettingsService);

		if ( account && this.currentAccountId != account.accountId ) // this policy is tested for almost every method, so lets pre-test it here
		{
			this.resetPromiseVariables();
			this.unexpectedPbxExists = undefined;
		  	this.currentAccountId = account.accountId;
		  	this.mivbInNetworkElements = false;
		  	this.capPromise = this.capService.isCAPFeatureEnabled(account).then( enabled => {
				this.capIsEnabled = enabled;
				return this.capIsEnabled;
			});

			this.mivbInNetworkElementsPromise = new Promise(resolve => {
				this.mivbAccountSettingsService.isMiVBInNetworkElementsObservable.subscribe(value => {
					this.mivbInNetworkElements = value;
					resolve(value);
				});
			});	

			this.initPromises = [this.capPromise, this.mivbInNetworkElementsPromise];
		}

		if (!this.meetService)
			this.meetService = AppInjector.injector.get(IntegMiteamMeetingsService);

		if (!this.appClaimsService)
			this.appClaimsService = AppInjector.injector.get(AppUserClaimsService);

		if (!this.spinnerService)
			this.spinnerService = AppInjector.injector.get(SpinnerService);

		if ( !this.dialog )
			this.dialog = AppInjector.injector.get(MatDialog);

		if ( !this.userBulkEditService )
			this.userBulkEditService = AppInjector.injector.get(UsersBulkEditMiVBService);

		if ( !this.mivbSharedSvc )
			this.mivbSharedSvc = AppInjector.injector.get(UserMiVBSharedService);

		if ( !this.orderService )
			this.orderService = AppInjector.injector.get(OrdersService);

		this.uiPreviewToggleService ??= AppInjector.injector.get(UIPreviewToggleService);
		this.zappaAccountService ??= AppInjector.injector.get(ZappaAccountService);

		if(this.initPromises.length > 0){
			return Promise.all(this.initPromises).then (() => {
				this.resetPromiseVariables();
			});
		}
		else{
			return Promise.resolve();
		}		
	}

	private resetPromiseVariables(){
		this.initPromises = [];
		this.capPromise = undefined;
		this.mivbInNetworkElementsPromise = undefined;
	}

	getConfigurationDialog() {
		return undefined;
	}

	isSetupComplete(account: Account): Promise<boolean> {
		return Promise.resolve(true);
	}

	async handleIntegrationStateChange(account: Account, state: boolean, productsPriorChange?: string[]): Promise<any> {
		// remove user licenses
		// the tags and policy will be removed by the subfeature that created them
		await this.initServices( account );
		if ( state == false )
		{
			// remove the user licenses
			return this.removeUserLicenses(account).then(val => {
				this.orderService.remainingLicensesChanged.next(); // need the license display to update
				return true;
			})
		}
		else
			return Promise.resolve(true);
	}

	removeUserLicenses ( account: Account )
	{
		return this.adminService.getUsersByAccountIdWithoutSpinner(account.accountId, {$Expand: 'tags'}).then(users=>{

			//checks if there is miVoice business subscription, if yes then licenses are assigned to users or not
			return this.mivbSharedSvc.getRemainingLicenseDetails().then( licenses => {
				this.spinnerService.hideSpinner();
				if ( Object.keys(licenses).length > 0 )
				{
						let assignedUserLicenses : boolean;
						Object.keys(licenses).forEach( license => {
						if(licenses[license].used != 0){
							assignedUserLicenses = true;
						}
					});

					//if there are users with the licenses assigned, then remove the licenses
					if ( assignedUserLicenses )
					{
						// now remove all the licenses from all users
						return this.userBulkEditService.saveUserMiVoiceBusinessBulkOperation(
							account.accountId, users, {license: "unassignAll"}).then(result => {
							return Promise.resolve();
						});
					}
				}
				return Promise.resolve();
			});
		})
		.catch( err => {
			// delete is best effort. Log and continue
			console.error("Failed removing MiVB licenses from the users", err);
			return Promise.resolve();
		})
	}

	async getLeftNavMenus(account: Account): Promise<MenuItem[]> {
		await this.initServices( account );
		// this menu is driven by a state of a sub feature.
		// Possible framework change in the future to allow subfeatures to provide menus?

		//In the future might want to change the condition to look for both CAP AND MiVB in network elements
		if ( this.capIsEnabled == false )
			return [];
		else {
			const systemSettingsItem = {
				name: 'SystemSettings',
				translationKey: 'SystemSettings',
				id: 'SystemSettings',
				minRole: UserRoles.ACCOUNT_ADMIN,
				navigation: `/accounts/${account.accountId}/mivb/systemSettings`,
				isFederatedSPA: true
			};

			const departmentsAndLocationsItem = {
				name: 'DepartmentsAndLocations',
				translationKey: 'DepartmentsAndLocations',
				id: 'DepartmentsAndLocations',
				minRole: UserRoles.ACCOUNT_ADMIN,
				navigation: `/accounts/${account.accountId}/mivb/departments-and-locations`,
				isFederatedSPA: true				
			};

			const numbersItem = {
				name: 'Numbers',
				translationKey: 'Numbers',
				id: 'Numbers',
				minRole: UserRoles.ACCOUNT_ADMIN,
				navigation: `/accounts/${account.accountId}/mivb/numbers`,
				isFederatedSPA: true				
			};

			const emergencyServicesItem = {
				name: `EmergencyServices`,
				translationKey: 'EmergencyServices',
				id: `EmergencyServices`,
				minRole: UserRoles.ACCOUNT_ADMIN,
				navigation: `/accounts/${account.accountId}/mivb/emergency-services`,
				isFederatedSPA: true
			};

			const voiceGatewayItem = {
				name: 'VoiceGateway',
				translationKey: 'VoiceGateway',
				id: 'VoiceGateway',
				minRole: UserRoles.ACCOUNT_ADMIN,
				navigation: `/accounts/${account.accountId}/mivb/voice-gateway`,
				isFederatedSPA: true				
			};

			// For now, the tools menu item is only shown if MiCollab is present
			// in the future, we might want to show always (if more than the MiCollab Sync component lives on the page)
			const toolsItem = {
				name: 'Tools',
				translationKey: 'Tools',
				id: 'Tools',
				minRole: UserRoles.ACCOUNT_ADMIN,
				navigation: `/accounts/${account.accountId}/mivb/tools`,
				isFederatedSPA: true				
			};

			const groupManagementItem = {
				name: 'GroupManagement',
				translationKey: 'GroupManagement',
				id: 'GroupManagement',
				minRole: UserRoles.ACCOUNT_ADMIN,
				navigation: `/accounts/${account.accountId}/mivb/groups`,
				isFederatedSPA: true				
			};

			const queueManagementItem = {
				name: 'QueueManagement',
				translationKey: 'QueueManagement',
				id: 'QueueManagement',
				minRole: UserRoles.ACCOUNT_ADMIN,
				navigation: `/accounts/${account.accountId}/mivb/queues`,
				isFederatedSPA: true				
			};
	
			const miVBManagementItem = {
				name: 'MiVoiceBusiness',
				translationKey: 'MiVoiceBusiness',
				id: "MiVoiceBusiness",
				minRole: UserRoles.ACCOUNT_ADMIN,
				navigation: "/norouting",
				iconActive: '../../../../assets/images/users_active.svg',
				iconInactive: '../../../../assets/images/users_inactive.svg',
				children: []
			};

			let isNewSystemSettingsUIEnabled = this.uiPreviewToggleService.isPreviewEnabled(UIPreviewKey.SYSTEM_SETTINGS);
			let acdGroupFlag = this.featureToggleService.isFeatureEnabled(FeatureFlags.ALLOW_MIVB_ACD_GROUPS);
			let allGroupsFlag = this.featureToggleService.isFeatureEnabled(FeatureFlags.ALLOW_ALL_GROUP_MGMT);
			let queueFlag = this.featureToggleService.isFeatureEnabled(FeatureFlags.ALLOW_MIVB_QUEUE_MGMT);

			if (isNewSystemSettingsUIEnabled) {
				miVBManagementItem.children.push(numbersItem);
				miVBManagementItem.children.push(departmentsAndLocationsItem);
			} else {
				miVBManagementItem.children.push(systemSettingsItem);
			}

			if ( acdGroupFlag || allGroupsFlag )
				miVBManagementItem.children.push(groupManagementItem);

			if ( queueFlag )
				miVBManagementItem.children.push(queueManagementItem);

			if (isNewSystemSettingsUIEnabled) {
				if (await this.zappaAccountService.doesAccountHaveZappaPhone(account.accountId).catch(() => false)) {
					miVBManagementItem.children.push(emergencyServicesItem);
				}
				miVBManagementItem.children.push(voiceGatewayItem);
				if (await this.mivbAccountSettingsService.isMiCollabPresent(account.accountId).catch(() => false)) {
					miVBManagementItem.children.push(toolsItem);
				}
			}
			return Promise.resolve( [miVBManagementItem] );
		}
	}

	canBeSelected(account: Account): Promise<boolean>{
		return Promise.resolve(true);
	}

	async canDeleteIntegration(account: Account): Promise<boolean>
	{
		await this.initServices( account );
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, { data: { mode: ConfirmationDialogs.MIVB_REMOVE_CONFIRMATION } });
		return dialogRef.afterClosed().toPromise().then(result => {
			return result;
		});

	}

	getConfigurationPageUrl(account: Account): string {
		return undefined;
	}

	async getStatusString(account: Account): Promise<StatusString> {
		await this.initServices( account );
		// displaying a generic error here.
		// think about showing an actual error if there is only one- would require more backend calls - performance concern?
		let parentStatus: StatusString = {
			status: 'integrations.mivb.subfeature-error', 
			type: StatusType.caution
		}

		return this.capService.getStatusString( account ).then( status => {
			if ( status == undefined )
			{
				return this.meetService.getStatusString( account ).then( status2 => {
					if ( status2 == undefined )
						return undefined;

					// if we get here there was an error
					return parentStatus;
				});
			}
			else
				// if we get here there was an error
				return parentStatus;
		})
		.catch( err => {
			console.log("Could not determine subfeature status", err);
			return parentStatus;
		})
	}

	getConfigButtonLabel(account: Account): Promise<string> {
		return undefined;
	}

	isButtonDisabled(account: Account): Promise<boolean> {
		return Promise.resolve(false);
	}

	canFilterUsersOnProduct(account: Account): boolean
	{
		return true;
	}

	async filterUsersBasedOnProduct(account: Account, users: User[], filter: any[] ): Promise<User[]>
	{
		await this.initServices( account );
		let newUsersList = new Array<User>();
	
		if ( this.capIsEnabled == false )
		{
			for ( var i = 0; i < users.length; i++ )
			{
				try
				{
					let licenses = await this.orderService.getUsersLicensesFromCachedList(users[i].userId);
					for ( var j = 0; j < licenses.length; j++ )
					{
						if(mivoiceBusinessLicenses.includes(licenses[j]))
						{
							newUsersList.push(users[i]);
						}  
					}
				}
				catch(e)
				{
					console.log("Error when retrieving licenses from a cached list. getUsersLicensesFromCachedList. Unlikely error", e)
				}
			}
		}
		else if(this.mivbInNetworkElements && !this.doesUnexpectedPbxExist(account))
		{
			for ( var i = 0; i < users.length; i++ )
			{
				if ( users[i].extension )
				{
					newUsersList.push(users[i]);
				}
			}
		}
		return newUsersList;
	}

	async getUserLicense( user: User, account: Account ): Promise<UserLicense>
	{
		await this.initServices( account );
		
		if ( this.capIsEnabled == false )
		{
			return this.orderService.getUsersLicensesFromCachedList(user.userId).then( licenses => {
				let userLicense: UserLicense;

				// if there is a legit subscription license
				for ( var i = 0; i < licenses.length; i++ )
				{
					if(mivoiceBusinessLicenses.includes(licenses[i])){
						userLicense = {
							code: licenses[i],
							displayStringKey: 'product_licenses.mivb.' + licenses[i]
						}
						return userLicense;
					}  
				}
				return undefined;
			});
		}
		else
		{
			// Current criteria to show a user has an mivb license:
			// - user has an extension
			// - CAP is enabled
			// - 3300ICP is found in network elements

			if ( user.extension && this.mivbInNetworkElements && !this.doesUnexpectedPbxExist(account)) {
				let userLicense: UserLicense;
				userLicense = {
					code: "mivb",
					displayStringKey: 'product_licenses.mivb.name'
				};
				return userLicense;
			} else {
				return undefined;
			}	
		}
	}

	private doesUnexpectedPbxExist(account: Account): boolean | undefined{
		if(this.unexpectedPbxExists === undefined){
			if(account && account.tags){
				//only care about unexpected pbx tags if CloudLink Gateway Integration is ON
				//similar logic in hasRequiredAccountIntegrations of user-prod-mivb.service.ts
				if(account.tags.products?.includes('Gateway')){
					let pbxsFoundInProductTags = Util.getPbxTypesFromTags(account.tags);
					if(pbxsFoundInProductTags && pbxsFoundInProductTags.length > 0){
						if(pbxsFoundInProductTags.length > 1 || pbxsFoundInProductTags[0] !== 'MiVoice Business'){
							this.unexpectedPbxExists = true;
							return this.unexpectedPbxExists; 
						}
					}
				}
				this.unexpectedPbxExists = false;
			}
		}
		return this.unexpectedPbxExists;
	}
}
